/*
  Modelo 3D (SketchUp) da página de produto (snippets/pdp-modelo-3d.liquid). O arquivo vem da Shopify ou do
  Google Drive (snippets/pdp-modelo-3d-url.liquid).

  Sem formulário do Hub nas configurações do tema, os links [data-modelo-3d-open] baixam direto. Com ele, o
  primeiro download abre a janela de cadastro: o arquivo baixa ao enviar e o contato vai para o Hub em segundo
  plano. O navegador guarda a pessoa, e os próximos modelos baixam direto, cada um registrado no Hub em nome dela.

  O envio usa o caminho do script de formulários do Hub (GET /f/config e POST /f/submit, em links.inusual.com.br).
  No formulário publicado, os campos são achados pelo tipo: nome, sobrenome, e-mail, WhatsApp, a escolha do perfil,
  o texto com a propriedade modelo_3d (recebe o nome da peça) e a caixa de consentimento de e-mail. Mudar textos ou
  a ordem no Hub não quebra o site; um campo que não existir lá só fica de fora. Com o formulário fora do ar, o
  download é direto. Se o envio falhar, o download acontece do mesmo jeito e o contato vai no próximo modelo baixado.
*/
(() => {
  if (customElements.get('modelo-3d-download')) return;

  const STORAGE_KEY = 'inusual:modelo-3d';
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const FIELDS = ['name', 'email', 'phone', 'perfil'];

  const loadLead = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null;
    } catch {
      return null;
    }
  };

  const saveLead = (lead) => {
    try {
      if (lead) localStorage.setItem(STORAGE_KEY, JSON.stringify(lead));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  // Endereço e loja vêm do script de formulários do Hub (app embed); sem ele, os de sempre.
  const hubScript = () => document.querySelector('script[src*="/f/forms.js"]');
  const hubEndpoint = () => (hubScript()?.dataset.endpoint || 'https://links.inusual.com.br').replace(/\/+$/, '');
  const hubShop = () => hubScript()?.dataset.shop || window.Shopify?.shop || '';

  // cache: 'no-cache' confere com o Hub a cada página: a Cloudflare entrega /f/config com 4 dias de cache no
  // navegador, e quem visitou o site antes de um formulário ser publicado continuaria sem ele.
  let configRequest = null;
  const findHubForm = (id) => {
    configRequest ??= fetch(`${hubEndpoint()}/f/config?shop=${encodeURIComponent(hubShop())}`, { credentials: 'omit', cache: 'no-cache' })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    return configRequest.then((config) => config?.forms?.find((form) => form.id === id) || null);
  };

  /** Respostas por bloco do formulário do Hub, achando cada campo pelo tipo. */
  const hubValues = (step, lead, { product, consent }) => {
    const blocks = step.blocks || [];
    const hasLastName = blocks.some((block) => block.type === 'field' && block.field === 'last_name');
    const [firstName, ...rest] = lead.name.split(' ');
    const values = {};
    for (const block of blocks) {
      if (block.type === 'consent') {
        if (block.channel === 'email') values[block.id] = consent;
        continue;
      }
      if (block.type !== 'field') continue;
      if (block.field === 'email') values[block.id] = lead.email;
      else if (block.field === 'phone' && lead.phone) values[block.id] = lead.phone;
      else if (block.field === 'first_name') values[block.id] = hasLastName ? firstName : lead.name;
      else if (block.field === 'last_name' && rest.length) values[block.id] = rest.join(' ');
      else if (block.field === 'text' && block.property === 'modelo_3d') values[block.id] = product;
      else if (block.field === 'choice') {
        const option = (block.options || []).find((item) => item.toLowerCase() === lead.perfil.toLowerCase());
        if (option) values[block.id] = option;
      }
    }
    return values;
  };

  const formStep = (form) => form?.def?.steps?.find((item) => item.kind === 'form') || null;

  /** Manda o contato ao Hub. true quando o Hub gravou. */
  const sendToHub = async (formId, lead, extra) => {
    const form = await findHubForm(formId);
    const step = formStep(form);
    if (!step) return false;

    let cid = null;
    try {
      cid = localStorage.getItem('_hub_cid');
    } catch {}

    const post = async (values) => {
      const response = await fetch(`${hubEndpoint()}/f/submit`, {
        method: 'POST',
        credentials: 'omit',
        keepalive: true,
        body: JSON.stringify({
          v: 1,
          shop: hubShop(),
          form: form.id,
          sid: crypto.randomUUID(),
          step: step.id,
          values,
          cid,
          url: location.href,
          device: matchMedia('(max-width: 740px)').matches ? 'mobile' : 'desktop',
          ms: extra.ms,
          hp: extra.hp || '',
        }),
      });
      return response.json();
    };

    try {
      const values = hubValues(step, lead, extra);
      let result = await post(values);
      // Campo recusado pelo Hub (ex.: telefone num formato que ele não aceita): manda de novo sem ele.
      const refused = Object.keys(result?.errors || {}).filter((key) => key in values && typeof values[key] !== 'boolean');
      if (!result?.ok && refused.length && !refused.some((key) => values[key] === lead.email)) {
        refused.forEach((key) => delete values[key]);
        result = await post(values);
      }
      if (!result?.ok) console.warn('[Modelo 3D] O Hub recusou o envio:', result?.errors);
      return !!result?.ok;
    } catch {
      return false;
    }
  };

  class Modelo3dDownload extends HTMLElement {
    connectedCallback() {
      // data-lead-form, não data-hub-form: o script do Hub desenha o formulário embutido em todo [data-hub-form].
      this.formId = (this.dataset.leadForm || '').trim();
      this.dialog = this.querySelector('[data-modelo-3d-dialog]');
      this.form = this.querySelector('[data-modelo-3d-form]');
      this.done = this.querySelector('[data-modelo-3d-done]');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };

      document.addEventListener('click', (event) => this.onClick(event), options);
      if (this.dialog) {
        this.form.addEventListener('submit', (event) => this.onSubmit(event), options);
        this.form.addEventListener('input', (event) => this.clearError(event.target.name), options);
        this.form.addEventListener('change', (event) => this.clearError(event.target.name), options);
        // Clique no fundo escurecido fecha (só quando o clique começou e terminou fora da janela).
        this.dialog.addEventListener('pointerdown', (event) => (this.pressedOutside = event.target === this.dialog), options);
        this.dialog.addEventListener('click', (event) => event.target === this.dialog && this.pressedOutside && this.dialog.close(), options);
        this.dialog.addEventListener('close', () => document.documentElement.classList.remove('pdp-3d-open'), options);
        this.querySelectorAll('[data-modelo-3d-close]').forEach((button) => button.addEventListener('click', () => this.dialog.close(), options));
      }
      this.querySelector('[data-modelo-3d-reset]')?.addEventListener('click', () => {
        saveLead(null);
        this.form?.reset();
        this.renderLead();
      }, options);
      this.renderLead();

      // Formulário do Hub fora do ar (ou Hub sem resposta): o download volta a ser direto.
      if (this.formId) {
        findHubForm(this.formId).then((form) => {
          this.hubReady = !!formStep(form);
          if (!this.hubReady) console.warn('[Modelo 3D] Formulário do Hub não encontrado ou fora do ar:', this.formId);
          this.renderLead();
        });
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    /** Pede o cadastro só com formulário do Hub no ar e navegador que abre a janela. */
    get gated() {
      return !!this.formId && this.hubReady !== false && typeof this.dialog?.showModal === 'function';
    }

    /** "Baixando como Fulana" quando o navegador já conhece a pessoa; nada quando o download é direto. */
    renderLead() {
      const lead = loadLead();
      const known = this.querySelector('[data-modelo-3d-known]');
      if (!known) return;
      known.hidden = !this.gated || !lead;
      if (lead) this.querySelector('[data-modelo-3d-name]').textContent = lead.name;
    }

    onClick(event) {
      if (!event.target.closest('[data-modelo-3d-open]')) return;
      const lead = this.gated ? loadLead() : null;
      if (this.gated && !lead) {
        event.preventDefault();
        this.open();
        return;
      }
      // O próprio link baixa; com cadastro, o Hub registra o modelo em nome da pessoa.
      this.track('modelo_3d_download');
      if (lead) this.record(lead);
    }

    open() {
      this.form.hidden = false;
      this.done.hidden = true;
      this.openedAt = Date.now();
      document.documentElement.classList.add('pdp-3d-open');
      this.dialog.showModal();
    }

    onSubmit(event) {
      event.preventDefault();
      const data = new FormData(this.form);
      const text = (name) => String(data.get(name) || '').trim().replace(/\s+/g, ' ');
      const lead = {
        name: text('name'),
        email: text('email').toLowerCase(),
        phone: text('phone'),
        perfil: text('perfil'),
        consent: data.get('consent') === 'on',
      };

      const errors = {};
      const digits = lead.phone.replace(/\D/g, '');
      if (lead.name.length < 2) errors.name = 'Digite seu nome.';
      if (!EMAIL.test(lead.email)) errors.email = lead.email ? 'Confira o e-mail.' : 'Digite seu e-mail.';
      if (lead.phone && (digits.length < 10 || digits.length > 13)) errors.phone = 'Confira o número, com DDD.';
      if (!lead.perfil) errors.perfil = 'Escolha uma opção.';
      if (this.showErrors(errors)) return;

      // Ainda dentro do clique do envio, para o navegador não barrar o download.
      this.download();
      this.track('modelo_3d_lead', { perfil: lead.perfil });
      this.track('modelo_3d_download');

      const hp = String(data.get('website') || '');
      if (!hp) {
        saveLead({ ...lead, at: new Date().toISOString(), synced: false, models: [] });
        this.renderLead();
      }
      this.send(lead, { consent: lead.consent, ms: Date.now() - this.openedAt, hp });

      this.form.hidden = true;
      this.done.hidden = false;
      this.done.focus();
    }

    /** Download de um modelo por quem o navegador já conhece: registra no Hub uma vez por peça. */
    record(lead) {
      if (lead.synced && lead.models?.includes(this.dataset.product)) return;
      // A caixa de e-mail só vai até o Hub gravar o primeiro envio. Depois quem decide é o Hub: a pessoa pode ter
      // saído da lista pelo e-mail, e um download novo não pode inscrever de novo.
      this.send(lead, { consent: !lead.synced && lead.consent, ms: Math.round(performance.now()) });
    }

    send(lead, extra) {
      const product = this.dataset.product;
      sendToHub(this.formId, lead, { ...extra, product: this.dataset.title }).then((ok) => {
        const current = loadLead();
        if (!ok || current?.email !== lead.email) return;
        saveLead({ ...current, synced: true, models: [...new Set([...(current.models || []), product])] });
      });
    }

    /** Arquivo da loja baixa na própria página; link do Drive abre numa aba nova, onde o download começa. */
    download() {
      const link = document.createElement('a');
      link.href = this.dataset.file;
      if (link.origin === location.origin) {
        link.download = '';
      } else {
        link.target = '_blank';
        link.rel = 'noopener';
      }
      link.hidden = true;
      document.body.append(link);
      link.click();
      link.remove();
    }

    track(name, data = {}) {
      window.dataLayer?.push({ event: name, product: this.dataset.product, ...data });
    }

    showErrors(errors) {
      let first = null;
      for (const name of FIELDS) {
        const message = this.form.querySelector(`[data-error-for="${name}"]`);
        const inputs = this.form.querySelectorAll(`[name="${name}"]`);
        message.textContent = errors[name] || '';
        message.hidden = !errors[name];
        inputs.forEach((input) => (errors[name] ? input.setAttribute('aria-invalid', 'true') : input.removeAttribute('aria-invalid')));
        first ||= errors[name] ? inputs[0] : null;
      }
      first?.focus();
      return !!first;
    }

    clearError(name) {
      const message = FIELDS.includes(name) && this.form.querySelector(`[data-error-for="${name}"]`);
      if (!message || message.hidden) return;
      message.hidden = true;
      this.form.querySelectorAll(`[name="${name}"]`).forEach((input) => input.removeAttribute('aria-invalid'));
    }
  }

  customElements.define('modelo-3d-download', Modelo3dDownload);
})();
